import {PLANS_ICONS} from '../constants'

// Groups a flat plan/addon/fee list by order_line.location_thryv_id (added to
// each item server-side by cc-order-dao.js's fetchOrderByUuid — see
// buildSendLinkPayload.js on the rep side for where the tag originates).
// Falls back to a single untitled group when the order predates this column,
// or was created via a caller that never sets it (single-location/Keap/
// proposal flows), so those orders keep rendering exactly as before — no
// location header shown when there's only one group.
//
// Items without their own tag (coupon/incentive lines synthesized client-side,
// BNI rows synthesized server-side, etc.) fall back to `fallbackLocationThryvId`
// — the order's own thryvId — mirroring the same `plan.locationThryvId ||
// order.thryv_id` convention the backend uses (see processMultiLocationPurchase
// in cc-order-service.js). Without this, a genuinely single-location order
// where every real plan is tagged with the order's thryvId but a synthesized
// line has no tag at all would split into two bogus groups: one for the
// tagged plans and a separate untitled one for the synthesized line.
export function groupItemsByLocation(items, fallbackLocationThryvId = null) {
  const order = []
  const byKey = {}
  items.forEach(item => {
    const resolvedLocationThryvId =
      item.locationThryvId ?? fallbackLocationThryvId ?? null
    const key = resolvedLocationThryvId ?? '__none__'
    if (!byKey[key]) {
      byKey[key] = {locationThryvId: resolvedLocationThryvId, items: []}
      order.push(key)
    }
    byKey[key].items.push(item)
  })
  return order.map((key, index) => ({...byKey[key], index}))
}

// Label for a location's accordion header. `locationNames` is the
// {[locationThryvId]: company} map from getOrderByUuid (cc-order-service.js),
// only ever populated for a genuinely multi-location order (2+ distinct
// tagged locations) — so a single-location order, a legacy order predating
// location tagging, or a billing-service lookup failure for this particular
// location all resolve the same way: fall back to the generic "Location N".
export function getLocationLabel(group, locationNames) {
  return (
    (group.locationThryvId && locationNames?.[group.locationThryvId]) ||
    `Location ${group.index + 1}`
  )
}

export function getPlanIcon(name) {
  const planItem = PLANS_ICONS.filter(item =>
    name.toLowerCase()?.includes(item.name),
  )
  return planItem[0]?.icon
}

export function createTransitionCoupon(durationAmount) {
  return {
    name: `25% off for ${durationAmount} month${durationAmount > 1 ? 's' : ''}`,
    isValid: true,
    description: `25% off for ${durationAmount} month${
      durationAmount > 1 ? 's' : ''
    }`,
    type: 'percent',
    duration: 'temporal',
    durationUnit: 'month',
    durationAmount: durationAmount,
    appliesToAllPlans: false,
    appliesToPlans: [
      'professional-4-dexyp-m2m',
      'unlimited-4-dexyp-m2m',
      'plus-4-dexyp-m2m',
      'unlimited-4-barbados-m2m',
      'professional-4-barbados-m2m',
      'plus-4-barbados-m2m',
      'plus-4-caymanislands-m2m',
      'professional-4-caymanislands-m2m',
      'unlimited-4-caymanislands-m2m',
      'addon-4-mc-m2m',
      'cc_plus-5-thryv-m2m',
      'cc_pro-5-thryv-m2m',
      'bc_plus-5-thryv-m2m',
      'bc_pro-5-thryv-m2m',
      'bc_unlm-5-thryv-m2m',
      'mc_pro-5-thryv-m2m',
      'mc_plus-5-thryv-m2m',
    ],
    isPercentDiscount: true,
    discountAmount: 0,
    discountPercent: 25,
    code: `25month${durationAmount}`,
  }
}

export function getLoadingMessage({
  calculatingTaxes,
  isVerifying,
  isFetching,
  isFetchingRecurly,
  isAus,
}) {
  if (calculatingTaxes) {
    return isAus ? 'Validating Details' : 'Calculating your Taxes & Fees'
  }
  if (isVerifying) {
    return 'Verifying CVC'
  }
  if (isFetching) {
    return 'Fetching card on file'
  }
  if (isFetchingRecurly) {
    return 'Processing'
  }
  return 'Processing'
}
